import React, { Fragment, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { HeaderPortal } from "../../components/ui-components/HeaderSlot.jsx";
import Badge from "../../components/ui-components/Badge";
import KpiCard from "../../components/ui-components/KpiCard";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import { toast } from "../../components/ui-components/Toast";
import RateSparkline from "./RateSparkline";
import { fmtMoney, fmtDate, sourceLabel, confidenceBadge, exceptionTone } from "./invoiceFormat";
import { listInvoices, getInvoiceStats, getInvoiceCounts, getAutomation, setAutomation } from "./invoiceReviewApi";

const TABS = [
  { key: "needs_review", label: "Needs Review", statusList: "pending,needs_review" },
  { key: "auto_processed", label: "Auto Processed", statusList: "confirmed", autoOnly: true },
  { key: "rejected", label: "Rejected", statusList: "rejected" },
  { key: "all", label: "All", statusList: "pending,needs_review,confirmed,rejected" },
];
// invoiceFormat tone -> Badge status (tone-driving) value.
const TONE_STATUS = { ok: "success", attention: "warning", critical: "danger", neutral: "neutral" };

const InvoiceProcessingPage = () => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState("needs_review");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("received");

  const active = TABS.find((t) => t.key === tab);
  const { data: stats } = useQuery(["invoice-stats"], getInvoiceStats);
  const { data: counts } = useQuery(["invoice-counts"], getInvoiceCounts);
  const { data: automation } = useQuery(["invoice-automation"], getAutomation);
  const { data: rows = [], isLoading, error } = useQuery(
    ["invoices", active.statusList],
    () => listInvoices(active.statusList)
  );

  const autoMut = useMutation((enabled) => setAutomation({ enabled }), {
    onMutate: async (on) => {
      await qc.cancelQueries(["invoice-automation"]);
      const prev = qc.getQueryData(["invoice-automation"]);
      qc.setQueryData(["invoice-automation"], (prevData) => ({ ...prevData, enabled: on }));
      return { prev };
    },
    onError: (_e, _on, ctx) => { qc.setQueryData(["invoice-automation"], ctx?.prev); toast.error("Couldn't update automation."); },
    onSettled: () => qc.invalidateQueries(["invoice-automation"]),
  });

  const filtered = useMemo(() => {
    let list = rows;
    if (active.autoOnly) list = list.filter((r) => r.auto_processed);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((r) => (r.vendor || "").toLowerCase().includes(q) || (r.invoice_number || "").toLowerCase().includes(q));
    return [...list].sort((a, b) =>
      sort === "amount" ? (b.amount_cents || 0) - (a.amount_cents || 0)
        : String(b.created_at || "").localeCompare(String(a.created_at || "")));
  }, [rows, active, search, sort]);

  const columns = useMemo(() => [
    { accessor: "vendor", Header: () => <span>Vendor</span>, Cell: ({ value }) => <span className="text-text truncate">{value || "—"}</span>, flex: 1, minWidth: 150 },
    { accessor: "invoice_number", Header: () => <span>Invoice #</span>, Cell: ({ value }) => <span className="text-text-2">{value || "—"}</span>, width: 130, minWidth: 110 },
    { id: "amount", Header: () => <span>Amount</span>, accessor: (r) => fmtMoney(r.amount_cents, r.currency), Cell: ({ value }) => <span className="text-text tabular-nums">{value}</span>, width: 130, minWidth: 110 },
    { accessor: "invoice_date", Header: () => <span>Invoice date</span>, Cell: ({ value }) => <span className="text-text-2">{fmtDate(value)}</span>, width: 120, minWidth: 100 },
    { accessor: "created_at", Header: () => <span>Received</span>, Cell: ({ value }) => <span className="text-text-2">{fmtDate(value)}</span>, width: 120, minWidth: 100 },
    { accessor: "exception_reason", Header: () => <span>Exception</span>, Cell: ({ value }) => value ? <Badge label={value} status={TONE_STATUS[exceptionTone(value)]} size="sm" /> : <span className="text-text-3">—</span>, width: 170, minWidth: 140 },
    { accessor: "confidence", Header: () => <span>Confidence</span>, Cell: ({ value }) => { const b = confidenceBadge(value); return <Badge label={b.label} status={TONE_STATUS[b.tone]} size="sm" />; }, width: 120, minWidth: 100 },
    { accessor: "source", Header: () => <span>Source</span>, Cell: ({ value }) => <span className="text-text-2">{sourceLabel(value)}</span>, width: 100, minWidth: 80 },
    { id: "review", align: "right", Header: () => null, Cell: ({ row }) => (
        <button type="button" onClick={() => navigate(`/invoice-review/${row.original.id}?status=${encodeURIComponent(active.statusList)}`)}
          className="px-3 py-1 text-sm rounded-ctrl text-primary hover:bg-primary-soft transition-colors">Review</button>
      ), width: 100, minWidth: 90 },
  ], [navigate, active]);

  const filterOptions = TABS.map((t) => ({ value: t.key, label: counts ? `${t.label} (${counts[t.key] ?? 0})` : t.label }));
  const rate = stats?.automation_rate_today;

  return (
    <Fragment>
      <HeaderPortal slot="title">
        <div className="flex items-center gap-3">
          <div className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text">Invoices</div>
          {typeof counts?.needs_review === "number" && counts.needs_review > 0 && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-warning-soft text-warning">
              {counts.needs_review} awaiting review
            </span>
          )}
        </div>
      </HeaderPortal>

      <HeaderPortal slot="actions">
        <div className="flex items-center gap-2.5">
          <button type="button" onClick={() => navigate("/invoice-review/settings")}
            className="px-3 py-1 rounded-ctrl text-sm border border-solid border-card-bd text-text-2 hover:bg-surface-2 transition-colors">
            Automation settings
          </button>
          <button type="button"
            onClick={() => { qc.invalidateQueries(["invoice-stats"]); qc.invalidateQueries(["invoice-counts"]); qc.invalidateQueries(["invoices"]); }}
            className="px-3 py-1 rounded-ctrl text-sm border border-solid border-card-bd text-text-2 hover:bg-surface-2 transition-colors">
            Refresh
          </button>
        </div>
      </HeaderPortal>

      <HeaderPortal slot="row2-right">
        <div className="flex items-center gap-2 text-sm text-text-2">
          <span>Automation</span>
          <button type="button" onClick={() => autoMut.mutate(!automation?.enabled)}
            aria-pressed={!!automation?.enabled}
            className={`px-3 py-1 rounded-ctrl text-sm border border-solid transition-colors ${automation?.enabled ? "bg-success-soft text-success border-success/30" : "bg-surface-2 text-text-3 border-card-bd"}`}>
            {automation?.enabled ? "On" : "Off"}
          </button>
        </div>
      </HeaderPortal>
      <div className="text-left flex flex-auto flex-col ml-10 mt-6 mr-6">
        <div className="mb-4">
          <p className="text-sm text-text-2 max-w-2xl mt-1">
            AI extracts invoice data from inbound email and processes invoices automatically whenever possible. Only exceptions require review.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <KpiCard label="Needs Review" tone="attention" value={stats?.needs_review ?? "—"} />
          <KpiCard label="Auto Processed" tone="ok" value={stats?.auto_processed_today ?? "—"} sub="today" />
          <KpiCard label="Rejected" tone="neutral" value={stats?.rejected ?? "—"} />
          <KpiCard label="Automation Rate" tone="neutral"
            value={rate == null ? "—" : `${Math.round(rate * 100)}%`}
            chart={<RateSparkline points={stats?.sparkline || []} />} />
        </div>

        <TableCard
          title="Queue"
          subtitle={isLoading ? "Loading…" : `${filtered.length} shown`}
          filterValue={tab}
          onFilterChange={setTab}
          filterOptions={filterOptions}
          filterAriaLabel="Invoice tab"
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search vendor or invoice #"
          controls={
            <select value={sort} onChange={(e) => setSort(e.target.value)}
              className="rounded-ctrl border border-card-bd bg-card px-2 py-1.5 text-[13px] text-text">
              <option value="received">Sort: Received</option>
              <option value="amount">Sort: Amount</option>
            </select>
          }
          isLoading={isLoading}
          error={error}
          errorMessage="Error loading invoices"
          onRetry={() => qc.invalidateQueries(["invoices", active.statusList])}
        >
          {filtered.length === 0 ? (
            <EmptyState icon={icon({ name: "file-invoice", style: "light" })} title="Nothing here" message="Invoices in this tab will show up here." />
          ) : (
            <FlexTable columns={columns} data={filtered} getRowId={(i) => i.id} />
          )}
        </TableCard>
      </div>
    </Fragment>
  );
};
export default InvoiceProcessingPage;
